"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Check } from "lucide-react";
import { useLang } from "@/contexts/language-context";
import AnimatedSection, { AnimatedElement } from "./AnimatedSection";

type Vertical = "restaurant" | "nightlife";

type Plan = {
  id: string;
  name: string;
  priceLabel: string;
  cadence: { en: string; es: string };
  badge?: { en: string; es: string };
  features: { en: string; es: string }[];
  cta: { en: string; es: string };
  ctaHref: string;
  highlight?: boolean;
  tone: "light" | "dark" | "violet";
};

const RESTAURANT_PLANS: Plan[] = [
  {
    id: "free",
    name: "Redy Start",
    priceLabel: "$0",
    cadence: { en: "/month software — with Redy payments", es: "/mes software — con pagos de Redy" },
    features: [
      { en: "Full POS on iPad", es: "POS completo en iPad" },
      { en: "Kitchen Display (KDS)", es: "Pantalla de cocina (KDS)" },
      { en: "Dual pricing included", es: "Dual pricing incluido" },
      { en: "Offline cash sales", es: "Ventas en efectivo offline" },
    ],
    cta: { en: "Get started free", es: "Comenzar gratis" },
    ctaHref: "/signup",
    tone: "light",
  },
  {
    id: "pro",
    name: "Redy Pro",
    priceLabel: "$97",
    cadence: { en: "/month · unlimited devices", es: "/mes · dispositivos ilimitados" },
    badge: { en: "Grow", es: "Crecer" },
    features: [
      { en: "Everything in Start and Plus", es: "Todo lo de Start y Plus" },
      { en: "Inventory, payroll & reports", es: "Inventario, nómina y reportes" },
      { en: "Website + custom domain", es: "Sitio web + dominio" },
      { en: "Loyalty program + CRM", es: "Programa de lealtad + CRM" },
    ],
    cta: { en: "Start Redy Pro", es: "Comenzar Redy Pro" },
    ctaHref: "/signup?plan=pro",
    highlight: true,
    tone: "dark",
  },
];

const NIGHTLIFE_PLANS: Plan[] = [
  {
    id: "nightlife",
    name: "Redy Nightlife",
    priceLabel: "$147",
    cadence: { en: "/month · for bars & lounges", es: "/mes · para bares y lounges" },
    features: [
      { en: "VIP Table Map (live)", es: "VIP Table Map (en vivo)" },
      { en: "QR door check-in", es: "Check-in QR en puerta" },
      { en: "Venue microsite", es: "Microsite del venue" },
      { en: "Event ticketing", es: "Venta de tickets" },
      { en: "Dual pricing included", es: "Dual pricing incluido" },
    ],
    cta: { en: "Start Nightlife", es: "Comenzar Nightlife" },
    ctaHref: "/signup?vertical=nightlife&plan=nightlife",
    tone: "violet",
  },
  {
    id: "nightlife-pro",
    name: "Redy Nightlife PRO",
    priceLabel: "$197",
    cadence: { en: "/month · for clubs & live events", es: "/mes · para clubs y eventos" },
    badge: { en: "Scale", es: "Escalar" },
    features: [
      { en: "Everything in Nightlife", es: "Todo lo de Nightlife" },
      { en: "Promoter links + commission tracking", es: "Links de promotores + control de comisiones" },
      { en: "Realtime dashboard per night", es: "Dashboard realtime por noche" },
      { en: "Live bar tabs + bottle service", es: "Tabs de barra en vivo + bottle service" },
    ],
    cta: { en: "Start Nightlife PRO", es: "Comenzar Nightlife PRO" },
    ctaHref: "/signup?vertical=nightlife&plan=nightlife-pro",
    highlight: true,
    tone: "dark",
  },
];

export default function PricingSection() {
  const { t } = useLang();
  const router = useRouter();
  const [vertical, setVertical] = useState<Vertical>("restaurant");

  const plans = vertical === "nightlife" ? NIGHTLIFE_PLANS : RESTAURANT_PLANS;
  const isNight = vertical === "nightlife";

  return (
    <section
      id="pricing"
      className={`py-32 px-6 overflow-hidden transition-colors duration-500 ${
        isNight ? "bg-[#0F0B1F]" : "bg-[#FAFAFA]"
      }`}
    >
      <div className="max-w-[1200px] mx-auto">
        <AnimatedSection className="text-center mb-12">
          <div
            className={`text-[11px] font-bold tracking-widest uppercase mb-4 ${
              isNight ? "text-[#F472B6]" : "text-redy-orange"
            }`}
          >
            {t("Pricing", "Precios")}
          </div>
          <h2
            className={`text-4xl md:text-[52px] font-black tracking-tight leading-[1.05] mb-4 ${
              isNight ? "text-white" : "text-redy-charcoal"
            }`}
          >
            {isNight ? (
              <>
                {t("Pick your night.", "Elige tu noche.")}
                <br />
                <span className="text-white/30">
                  {t("No per-ticket platform fee.", "Sin fee de plataforma por boleto.")}
                </span>
              </>
            ) : (
              <>
                {t("Start Free.", "Empieza Gratis.")}
                <br />
                <span className="text-black/30">
                  {t("Dual Pricing Built In.", "Dual Pricing Incluido.")}
                </span>
              </>
            )}
          </h2>
          <p
            className={`text-[15px] font-medium max-w-xl mx-auto mb-10 ${
              isNight ? "text-white/55" : "text-redy-secondary"
            }`}
          >
            {isNight
              ? t(
                  "Two nightlife plans. Both include dual pricing and the North terminal is yours to keep. Pick the tier that fits the kind of nights you run.",
                  "Dos planes nightlife. Ambos incluyen dual pricing y el terminal North es tuyo. Elige el tier que se acomoda a las noches que manejas."
                )
              : t(
                  "Dual pricing is included in every plan. Start is free when you process payments with Redy (activation subject to payments account approval); Plus ($49) and Pro ($97) when you need more. Add a one-time North terminal — yours to keep.",
                  "Dual pricing incluido en todos los planes. Start es gratis si procesas tus pagos con Redy (la activación depende de la aprobación de tu cuenta de pagos); Plus ($49) y Pro ($97) cuando necesites más. Suma un terminal North de pago único — tuyo."
                )}
          </p>

          <div
            className={`inline-flex p-1.5 rounded-full ${
              isNight ? "bg-white/5 border border-white/10" : "bg-white border border-black/5 shadow-sm"
            }`}
          >
            <button
              onClick={() => setVertical("restaurant")}
              className={`relative px-6 py-2.5 rounded-full text-[13px] font-bold transition-colors cursor-pointer ${
                !isNight ? "text-white" : "text-white/55 hover:text-white"
              }`}
            >
              {!isNight && (
                <motion.div
                  layoutId="pricing-vertical-pill"
                  className="absolute inset-0 bg-redy-charcoal rounded-full"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative">🍽 {t("Restaurant", "Restaurant")}</span>
            </button>
            <button
              onClick={() => setVertical("nightlife")}
              className={`relative px-6 py-2.5 rounded-full text-[13px] font-bold transition-colors cursor-pointer ${
                isNight ? "text-white" : "text-redy-charcoal/55 hover:text-redy-charcoal"
              }`}
            >
              {isNight && (
                <motion.div
                  layoutId="pricing-vertical-pill"
                  className="absolute inset-0 rounded-full"
                  style={{ backgroundImage: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)" }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative">🌃 {t("Nightlife", "Nightlife")}</span>
            </button>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[900px] mx-auto items-stretch">
          {plans.map((plan, i) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              delay={0.1 * i}
              onSelect={() => router.push(plan.ctaHref)}
            />
          ))}
        </div>

        <AnimatedSection delay={0.3} className="mt-12 text-center">
          <Link
            href="/pricing"
            className={`inline-flex items-center gap-1.5 text-[13px] font-bold transition-colors ${
              isNight ? "text-white/55 hover:text-white" : "text-redy-secondary hover:text-redy-charcoal"
            }`}
          >
            {t("See full plan comparison →", "Ver comparación completa →")}
          </Link>
        </AnimatedSection>
      </div>
    </section>
  );
}

function PlanCard({
  plan,
  delay,
  onSelect,
}: {
  plan: Plan;
  delay: number;
  onSelect: () => void;
}) {
  const { t } = useLang();

  const cardClass =
    plan.tone === "light"
      ? "bg-white border border-black/5 shadow-[0_20px_60px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)]"
      : plan.tone === "violet"
        ? "border border-white/10 shadow-2xl"
        : "bg-redy-charcoal shadow-2xl";

  const cardStyle =
    plan.tone === "violet" ? { backgroundColor: "rgba(255,255,255,0.04)" } : undefined;

  const eyebrowColor = plan.tone === "light" ? "text-black/30" : "text-white/40";
  const priceColor = plan.tone === "light" ? "text-redy-charcoal" : "text-white";
  const cadenceColor = plan.tone === "light" ? "text-redy-secondary" : "text-white/50";
  const divider = plan.tone === "light" ? "bg-black/5" : "bg-white/10";
  const checkBg =
    plan.tone === "light"
      ? "bg-[#FAFAFA] border border-black/5"
      : "bg-white/10 border border-white/5";
  const checkColor = plan.tone === "light" ? "text-redy-charcoal" : "text-redy-orange";
  const labelColor = plan.tone === "light" ? "text-redy-charcoal/80" : "text-white/90";

  const ctaClass =
    plan.tone === "light"
      ? "bg-[#FAFAFA] border border-black/5 text-redy-charcoal hover:bg-black/5"
      : plan.tone === "violet"
        ? "text-white"
        : "bg-redy-orange text-white hover:scale-[1.02] shadow-lg shadow-redy-orange/20";

  const ctaStyle =
    plan.tone === "violet"
      ? {
          backgroundImage: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
          boxShadow: "0 10px 30px rgba(124, 58, 237, 0.35)",
        }
      : undefined;

  return (
    <AnimatedElement
      delay={delay}
      className={`rounded-[40px] p-10 flex flex-col relative overflow-hidden group transition-all ${cardClass}`}
      style={cardStyle}
    >
      {plan.tone === "dark" && (
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-redy-orange/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      )}
      {plan.tone === "violet" && (
        <>
          <div className="absolute top-0 right-0 w-[280px] h-[280px] bg-[#7C3AED]/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#F472B6]/15 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/3 pointer-events-none" />
        </>
      )}

      <div className="flex justify-between items-start mb-6 relative z-10">
        <div className={`text-[14px] font-black tracking-widest uppercase ${eyebrowColor}`}>
          {plan.name}
        </div>
        {plan.badge && (
          <div
            className={`text-[10px] font-black tracking-widest uppercase px-3 py-1.5 rounded-full ${
              plan.tone === "violet" ? "text-white" : "bg-redy-orange text-white"
            }`}
            style={
              plan.tone === "violet"
                ? { backgroundImage: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)" }
                : undefined
            }
          >
            {t(plan.badge.en, plan.badge.es)}
          </div>
        )}
      </div>

      <div className={`text-6xl font-black tracking-tight mb-2 relative z-10 ${priceColor}`}>
        {plan.priceLabel}
      </div>
      <div className={`text-[14px] font-bold mb-6 relative z-10 ${cadenceColor}`}>
        {t(plan.cadence.en, plan.cadence.es)}
      </div>

      <div className={`h-[1px] w-full mb-8 ${divider}`} />

      <div className="flex flex-col gap-5 flex-1 mb-10 relative z-10">
        {plan.features.map((f, i) => (
          <div key={i} className="flex items-center gap-4">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${checkBg}`}>
              <Check className={`w-3.5 h-3.5 stroke-[3] ${checkColor}`} />
            </div>
            <span className={`text-[15px] font-bold ${labelColor}`}>
              {t(f.en, f.es)}
            </span>
          </div>
        ))}
      </div>

      <button
        onClick={onSelect}
        className={`w-full py-4 rounded-full font-bold text-[15px] transition-all relative z-10 cursor-pointer ${ctaClass}`}
        style={ctaStyle}
      >
        {t(plan.cta.en, plan.cta.es)}
      </button>
    </AnimatedElement>
  );
}
